import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  CreditCard,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  ImageOff,
} from "lucide-react";
import { Badge } from "../../../components/badge";
import { Card, CardContent } from "../../../components";
import type { MerchantDetail } from "../types/merchant.types";
import {
  formatMerchantDate,
  getMerchantDisplayName,
  getMerchantStatus,
} from "../utils/merchant.utils";

type MerchantDetailsViewProps = {
  merchant: MerchantDetail;
};

type DetailMetricProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  detail?: ReactNode;
};

const DetailMetric = ({ icon, label, value, detail }: DetailMetricProps) => (
  <div className="flex min-w-0 gap-3 px-4 py-3 sm:px-5">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
      {detail ? (
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{detail}</p>
      ) : null}
    </div>
  </div>
);

const SectionHeading = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
      {icon}
    </div>
    <h2 className="text-base font-bold text-slate-900 dark:text-white">{children}</h2>
  </div>
);

const getStatusColor = (status: string) => {
  switch (status) {
    case "verified":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "suspended":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
  }
};

export default function MerchantDetailsView({ merchant }: MerchantDetailsViewProps) {
  const { t } = useTranslation();
  const status = getMerchantStatus(merchant);
  const ownerName = [merchant.first_name, merchant.last_name].filter(Boolean).join(" ") || "—";

  return (
    <main className="min-h-full bg-slate-50 px-4 py-6 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Card className="border-slate-200 shadow-sm dark:border-slate-800">
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
            {merchant.logo_url ? (
              <img
                src={merchant.logo_url}
                alt={getMerchantDisplayName(merchant)}
                className="h-20 w-20 rounded-2xl object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                <ImageOff
                  className="h-8 w-8"
                  aria-hidden="true"
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-xl font-bold text-slate-900 dark:text-white">
                {getMerchantDisplayName(merchant)}
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t("merchant.owner")}: {ownerName}
              </p>
            </div>
            <Badge className={getStatusColor(status)}>{t(`merchant.${status}`)}</Badge>
          </CardContent>
        </Card>

        <Card className="mt-5 border-slate-200 shadow-sm dark:border-slate-800">
          <CardContent className="grid divide-y divide-slate-200 p-0 dark:divide-slate-800 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            <DetailMetric
              icon={
                <Mail
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
              label={t("merchant.email")}
              value={merchant.email || "—"}
            />
            <DetailMetric
              icon={
                <Phone
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
              label={t("merchant.phone")}
              value={merchant.phone || "—"}
            />
            <DetailMetric
              icon={
                <BadgeCheck
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
              label={t("merchant.badge_level")}
              value={merchant.badge_level}
              detail={
                merchant.last_reviewed_at
                  ? `${t("merchant.last_reviewed_at")}: ${formatMerchantDate(merchant.last_reviewed_at)}`
                  : undefined
              }
            />
            <DetailMetric
              icon={
                <CalendarDays
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              }
              label={t("merchant.created_at")}
              value={formatMerchantDate(merchant.created_at)}
            />
          </CardContent>
        </Card>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <div className="grid content-start gap-5">
            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading
                  icon={
                    <FileText
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  }
                >
                  {t("merchant.business")}
                </SectionHeading>
                <dl className="grid gap-3 text-sm">
                  {[
                    [t("merchant.short_intro"), merchant.short_intro || "—"],
                    [t("merchant.description"), merchant.description || "—"],
                    [t("merchant.review_note"), merchant.review_note || "—"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-start justify-between gap-4"
                    >
                      <dt className="shrink-0 text-slate-500 dark:text-slate-400">{label}</dt>
                      <dd className="text-right font-semibold text-slate-900 dark:text-white">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading
                  icon={
                    <MapPin
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  }
                >
                  {t("merchant.locations")}
                </SectionHeading>
                {merchant.locations.length ? (
                  <ul className="grid gap-3 text-sm">
                    {merchant.locations.map((location, index) => (
                      <li
                        key={location.id || `${location.address}-${index}`}
                        className="rounded-xl bg-slate-50 p-3 dark:bg-slate-900"
                      >
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {location.address || "—"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {location.city || ""}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t("merchant.no_locations")}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid content-start gap-5">
            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading
                  icon={
                    <Building2
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  }
                >
                  {t("merchant.profile")}
                </SectionHeading>
                <dl className="grid gap-3 text-sm">
                  {[
                    [t("merchant.partner_type"), merchant.partner_type || "—"],
                    [t("merchant.user_status"), merchant.user_status],
                    [
                      t("merchant.requires_reverification"),
                      merchant.requires_reverification ? t("common.yes") : t("common.no"),
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-start justify-between gap-4"
                    >
                      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                      <dd className="text-right font-semibold text-slate-900 dark:text-white">
                        {value}
                      </dd>
                    </div>
                  ))}
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-slate-500 dark:text-slate-400">
                      {t("merchant.business_url")}
                    </dt>
                    <dd className="text-right font-semibold text-slate-900 dark:text-white">
                      {merchant.business_url ? (
                        <a
                          href={merchant.business_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"
                        >
                          <Globe
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                          {merchant.business_url}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading
                  icon={
                    <CreditCard
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  }
                >
                  {t("merchant.subscription")}
                </SectionHeading>
                <dl className="grid gap-3 text-sm">
                  {[
                    [t("merchant.plan_name"), merchant.plan_name || "—"],
                    [t("merchant.plan_code"), merchant.plan_code || "—"],
                    [t("merchant.subscription_state"), merchant.subscription_state || "—"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="flex items-start justify-between gap-4"
                    >
                      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                      <dd className="text-right font-semibold text-slate-900 dark:text-white">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}
